import React, { useCallback } from 'react';
import { gql } from '@apollo/client';
import { PromoBanner } from '@moda/om';
import md5 from 'md5';
import { Link } from '../Link';
import {
  SitePromoBannerFragment,
  SitePromoBannerFragment_targetBuckets_modules_SinglePumoModule as SinglePumoModuleType,
  SitePromoBannerFragment_targetBuckets_modules_SinglePumoModule_slides as SinglePumoModuleSlide
} from '../../generated/types/SitePromoBannerFragment';
import { stripHTMLTags } from '../NormalizePumoStyles';
import { useSwiper } from '../Swiper';
import { tc } from '../../lib/trackingContext';
import { SitePromoBannerCountrySelector } from './SitePromoBannerCountrySelector';
import { SitePromoBannerLink } from './SitePromoBannerLink';

import './SitePromoBanner.scss';

export const SITE_PROMO_BANNER_FRAGMENT = gql`
  fragment SitePromoBannerFragment on DisplayPage {
    targetBuckets {
      name
      modules {
        ... on SinglePumoModule {
          slides {
            ctaUrl
            slideText {
              ctaText
              taglineText {
                body
              }
            }
          }
        }
      }
    }
  }
`;

const SITE_PROMO_BANNER_FALLBACK_MESSAGE =
  'Free Shipping & Returns in the U.S. | Use Code NEWTOMODA for 10% Off Your First Full-Price Purchase';

interface Props {
  displayPage?: SitePromoBannerFragment | null;
}

export const SitePromoBanner: React.FC<Props> = ({ displayPage }) => {
  const bannerSlides: SinglePumoModuleSlide[] | null = (displayPage?.targetBuckets.find(
    ({ name }) => name === 'banner'
  )?.modules[0] as SinglePumoModuleType)?.slides;

  const fallbackPromoBannerMessage = stripHTMLTags(SITE_PROMO_BANNER_FALLBACK_MESSAGE);

  useSwiper('.SitePromoBanner__main', {
    autoplay: { delay: 4000 },
    loop: true,
    slidesPerView: 1,
    centeredSlides: true,
    fadeEffect: { crossFade: true },
    effect: 'fade',
    speed: 1400
  });

  const trackHrefClick = useCallback((taglineText: string, promoId: string) => {
    tc.track('Promotion Clicked', {
      addToTrackingEvent: {
        position: 'promo_banner',
        name: taglineText,
        promotion_id: promoId
      }
    });
  }, []);

  return (
    <PromoBanner role="banner" className="SitePromoBanner">
      <div className="SitePromoBanner__cell">
        <SitePromoBannerCountrySelector />
      </div>

      {bannerSlides ? (
        <div className="SitePromoBanner__main swiper-container">
          <div className="SitePromoBanner__rail swiper-wrapper">
            {bannerSlides.map(slide => (
              <Link
                key={slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE}
                className="SitePromoBanner__cell SitePromoBanner__cta swiper-slide"
                to={slide?.ctaUrl || '#'}
                onClick={() =>
                  trackHrefClick(
                    slide?.slideText?.taglineText?.body || '',
                    slide?.slideText?.ctaText || md5(slide?.slideText?.taglineText?.body || '')
                  )
                }
                title={stripHTMLTags(
                  slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE
                )}
                dangerouslySetInnerHTML={{
                  __html: stripHTMLTags(
                    slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE
                  )
                }}
              />
            ))}
          </div>
        </div>
      ) : (
        <Link
          className="SitePromoBanner__cell SitePromoBanner__cta"
          to="#"
          title={fallbackPromoBannerMessage}
          dangerouslySetInnerHTML={{ __html: fallbackPromoBannerMessage }}
        />
      )}

      <div className="SitePromoBanner__cell">
        <SitePromoBannerLink />
      </div>
    </PromoBanner>
  );
};
